import { DateTime } from 'luxon';
import type {
  IProfessionAllTimeStats,
  IProfessionAnalysis,
  IProfessionAnalysisDay,
  IProfessionLocations,
  IProjectFloor,
} from '@apis/contractorAnalysis';
import { CONTRACTOR_RANGES, presetDays, type ContractorRange, type IDailyWorkersHours, type ILocationShare } from './types';

/* -------------------------------------------------------------------------- */
/* Empty payloads — used as `useQuery` placeholders so the page never has to  */
/* render `undefined`. The chart / panel render-empty paths kick in for both  */
/* "no data yet" and "still loading" indistinguishably.                       */
/* -------------------------------------------------------------------------- */

export const EMPTY_ALL_TIME: IProfessionAllTimeStats = {
  firstWorkingDayIso: null,
  totalWorkHours: 0,
  avgDailyWorkers: 0,
  avgDailyWorkHours: 0,
};

export const EMPTY_ANALYSIS: IProfessionAnalysis = { averageDayWorkDurationHours: 0, days: [] };

export const EMPTY_LOCATIONS: IProfessionLocations = { byFloor: [], byArea: [] };

/* -------------------------------------------------------------------------- */
/* Date window                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Inclusive YYYY-MM-DD window ending on today (project tz), spanning
 * `preset` days backwards. Matches trusstor's V2 default behaviour:
 * `moment().subtract(7, 'days').add(1, 'day')` → today inclusive.
 */
export function computeDateRangeFromToday(
  preset: ContractorRange,
  tz: string,
): { dateFrom: string; dateTo: string } {
  const end = DateTime.now().setZone(tz).startOf('day');
  const start = end.minus({ days: presetDays(preset) - 1 });
  return {
    dateFrom: start.toFormat('yyyy-MM-dd'),
    dateTo: end.toFormat('yyyy-MM-dd'),
  };
}

/** Initial dateFrom/dateTo for the page — today-anchored 7-day window. */
export function initialWindow(tz: string): { dateFrom: string; dateTo: string } {
  return computeDateRangeFromToday('7d', tz);
}

/**
 * Inverse of `computeDateRangeFromToday`: if the supplied dateFrom/dateTo
 * matches a preset window ending today, return the preset so the tabs
 * highlight stays in sync. Otherwise return null (custom range).
 */
export function matchPreset(
  dateFrom: string,
  dateTo: string,
  tz: string,
): ContractorRange | null {
  for (const preset of CONTRACTOR_RANGES) {
    const presetWindow = computeDateRangeFromToday(preset, tz);
    if (presetWindow.dateFrom === dateFrom && presetWindow.dateTo === dateTo) return preset;
  }
  return null;
}

/* -------------------------------------------------------------------------- */
/* Display / math helpers                                                      */
/* -------------------------------------------------------------------------- */

export function formatWorkingSince(iso: string | null, tz: string): string {
  if (!iso) return '';
  const dt = DateTime.fromISO(iso, { zone: tz });
  if (!dt.isValid) return '';
  return dt.toFormat('LLL yyyy');
}

export function averageOver<T>(arr: readonly T[], pick: (item: T) => number): number {
  if (arr.length === 0) return 0;
  return arr.reduce((acc, item) => acc + pick(item), 0) / arr.length;
}

/** Project a BFF day onto the chart-row shape used by `WorkersHoursChart`. */
export function projectAnalysisDay(day: IProfessionAnalysisDay): IDailyWorkersHours {
  return {
    date: day.date,
    workers: day.totalWorkers,
    hours: +day.totalWorkHours.toFixed(2),
  };
}

/* -------------------------------------------------------------------------- */
/* Location views                                                              */
/* -------------------------------------------------------------------------- */

export interface ILocationViews {
  /** Catalog-ordered rows, filtered to floors with non-zero hours. */
  rows: ILocationShare[];
  activeLocations: number;
}

/**
 * Convert the BFF's per-floor / per-area totals into the percentage rows the
 * Location Distribution panel renders. The project floor catalog drives BOTH
 * the rendered floor set AND its order — matches trusstor's V2 panel which
 * iterates `sortedFloors` (catalog) and looks up each floor's time from the
 * productivity records, silently dropping floors that don't have a matching
 * catalog entry. The d-orca productivity endpoint sometimes returns
 * historical floor ids that have since been removed from the catalog
 * (e.g. retired Hebrew-nicked scaffolds) — those drop out here.
 *
 * Percentages are computed against the productivity-records sum across ALL
 * floors returned upstream (not just the catalog-matched ones). That's the
 * same semantic as trusstor's `totalHoursForAllFloors`, which sums every key
 * in `floorHours` regardless of whether it's catalog-listed.
 */
export function buildLocationViews(
  locations: IProfessionLocations,
  floors: readonly IProjectFloor[],
): ILocationViews {
  const productivityByFloorId = new Map(
    locations.byFloor.map((floor) => [floor.floorId, floor.totalHours]),
  );

  const totalFloor = locations.byFloor.reduce((acc, floor) => acc + floor.totalHours, 0);

  const rows: ILocationShare[] = floors
    .map((floor) => {
      const hours = productivityByFloorId.get(floor.floorId);
      if (!hours) return null;
      return {
        id: floor.floorId,
        label: floor.floorNick,
        floor: floor.floorNick,
        percent: totalFloor > 0 ? +((hours / totalFloor) * 100).toFixed(1) : 0,
      };
    })
    .filter((row): row is ILocationShare => row !== null);

  return {
    rows,
    activeLocations: rows.length,
  };
}
